/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-one-expression-per-line */
import styled from '@emotion/styled';
import PropTypes from 'prop-types';
import UnderlineLink from '@comp/UnderlineLink/UnderlineLink';
import PriceHike from '@comp/PriceHike/PriceHike';
import { useSelector } from 'react-redux';
import H2 from '@base/H2/H2';
import H3 from '@base/H3/H3';
import H4 from '@base/H4/H4';
import H5 from '@base/H5/H5';
import BaseImage from '@base/Image';
import Divider from '@base/Divider/Divider';
import textHelper from '@utils/textHelper';
import boxShadowGenerator from '@utils/boxShadowGenerator';
import { CHECKOUT_STARTED } from '@constants/segment';
import { segment } from '@actions/analytics';
import useCheckoutStartedEvtProps from '@hooks/useCheckoutStartedEvtProps';
import { evtNameCategoryMap } from '@constants/index';
import useGoalPageStaticContent from '@api/hooks/goal/useGoalPageStaticContent';
import usePriceHikeInfo from '@api/hooks/goal/usePriceHikeInfo';
import { Spacings, Fonts, mediaQuery } from '@styles/index';
import TriangleUp from './TriangleUp';
import MobileIconicSection from './Iconic/MobileIconicSection';

const data = [
  {
    name: 'Access to curated test series',
    description:
      'Curated test series that lets you evaluate your preparation before your examination',
    image: 'goal/benefittest.svg'
  },
  {
    name: 'Daily practice questions',
    description:
      'Strengthen your concepts by practising daily questions and quizzes',
    image: 'goal/practice.svg'
  }
];

const SecWrapper = styled.section`
  width: 100%;
  position: relative;
  margin-top: 140px;
  @media (max-width: 47.95em) {
    display: none;
  }
`;

const Container = styled.div`
  background-color: var(--color-i-white);
  box-shadow: ${boxShadowGenerator()};
  border-radius: ${Spacings.SPACING_2B};
  ${({ hasIconicSection }) =>
    !hasIconicSection &&
    `top: -${Spacings.SPACING_10B}; margin-bottom: -${Spacings.SPACING_10B};`}
  position: relative;
  margin: 0 auto;
  text-align: center;
  width: 1136px;
  padding-top: ${Spacings.SPACING_20B};
  ${mediaQuery.uptoTablet} {
    width: 728px;
    padding-top: ${Spacings.SPACING_8B};
  }
`;

const Wrapper = styled.div`
  margin-top: ${Spacings.SPACING_12B};
  margin-bottom: -${Spacings.SPACING_8B};
  ${mediaQuery.aboveMobile} {
    display: none;
  }
`;

const CardsContainer = styled.div`
  display: flex;
  margin-left: ${Spacings.SPACING_24B};
  margin-right: ${Spacings.SPACING_24B};
  margin-top: 74px;
  flex-wrap: wrap;
  ${mediaQuery.aboveTablet} {
    & > :nth-child(even) {
      margin-left: ${Spacings.SPACING_24B};
    }
  }
  ${mediaQuery.uptoTablet} {
    display: grid;
    grid-template-columns: 1fr 1fr;
    grid-column-gap: 78px;
    grid-row-gap: ${Spacings.SPACING_8B};
    margin: 40px 58px 32px 61px;
  }
`;

const CardWrapper = styled.div`
  width: 416px;
  margin-bottom: 66px;
  text-align: left;
  display: flex;
  ${mediaQuery.uptoTablet} {
    width: 100%;
    margin-bottom: 0px;
  }
`;

const CardContentLeft = styled.div`
  margin-right: ${Spacings.SPACING_4B};
`;

const CardContentRight = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
`;

const Description = styled(H5)`
  font-weight: ${Fonts.REGULAR};
  margin-top: ${Spacings.SPACING_4B};
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.SMALL};
    margin-top: ${Spacings.SPACING_2B};
  }
`;

const Image = styled(BaseImage)`
  ${mediaQuery.uptoTablet} {
    width: ${Spacings.SPACING_12B};
    height: ${Spacings.SPACING_12B};
    object-fit: contain;
  }
`;

const SubHeading = styled(H5)`
  font-weight: ${Fonts.REGULAR};
  margin-top: ${Spacings.SPACING_2B};
`;

const CardTitle = styled(H3)`
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.LARGE};
    line-height: 150%;
  }
`;

const Card = ({ card }) => (
  <CardWrapper>
    <CardContentLeft>
      <Image width={80} height={34} src={card.image} alt={card.name} />
    </CardContentLeft>
    <CardContentRight>
      <CardTitle>{card.name}</CardTitle>
      <Description>{card.description}</Description>
    </CardContentRight>
  </CardWrapper>
);

const ExamPricing = styled.span`
  color: var(--color-i-blue);
  padding-left: 5px;
  font-weight: bold;
  font-size: ${Fonts.H3};
  line-height: 120%;
  ${mediaQuery.uptoTablet} {
    font-size: 22px;
    display: inline-block;
  }
`;

const PrciningVal = styled.div`
  width: 600px;
  text-align: left;
  ${mediaQuery.uptoTablet} {
    width: auto;
  }
`;

const Pricing = styled.div`
  padding: ${Spacings.SPACING_16B} ${Spacings.SPACING_24B};
  display: flex;
  justify-content: space-between;
  ${mediaQuery.uptoTablet} {
    padding: 32px 58px 32px 61px;
  }
`;

const LinkContainer = styled.div`
  align-self: center;
`;

const UNDivider = styled(Divider)`
  margin: 0 ${Spacings.SPACING_24B};
  ${mediaQuery.uptoTablet} {
    height: 2px;
    margin: 0 58px 0 61px;
  }
`;

const PricingTitle = styled(H3)`
  ${mediaQuery.uptoTablet} {
    font-size: 18px;
    display: inline-block;
  }
`;

const ViewPricing = styled(H4)`
  ${mediaQuery.uptoTablet} {
    font-size: ${Fonts.LARGE};
    line-height: 100%;
  }
`;

const LiteSection = ({
  block,
  goalUID,
  goalSlug,
  goalDetails,
  countryCode,
  dataAnalytics,
  hasIconicSection,
  blockPosition
}) => {
  const eventProps = useCheckoutStartedEvtProps(goalDetails);
  const { extraBlockInfo } = block;
  const { isNoTestGoal } = goalDetails;
  let { goalStaticContent: { liteBenefits: staticContent } = {} } =
    useGoalPageStaticContent(goalUID);
  const { goalStaticContent: defaultData } =
    useGoalPageStaticContent('__default');
  if (!staticContent && defaultData) staticContent = defaultData?.liteBenefits;

  let staticData =
    staticContent?.benefits?.length > 0 ? staticContent.benefits : data;
  if (isNoTestGoal && block?.data && block?.data.length === 4) {
    staticData = block.data.map((item, i) => {
      return {
        ...item,
        image: data[i].image
      };
    });
  }
  const goalCurrencyCode = textHelper.currencySymbolMap(countryCode);
  const handleAnalytics = () => {
    segment.track(CHECKOUT_STARTED, eventProps, evtNameCategoryMap.BOTH);
  };

  const partPaymentStatus = useSelector(
    ({ checkout }) => checkout.partPaymentStatus?.[goalUID]
  );

  const priceHikeInfo = usePriceHikeInfo(goalUID);

  const headerText = staticContent?.title || 'LITE subscription';
  const subHeading =
    staticContent?.subtitle || 'Assess your preparation with tests';

  return (
    <>
      <Wrapper>
        <MobileIconicSection
          type="Lite"
          info={{ heading: 'LITE', subHeading, data: staticData }}
        />
      </Wrapper>

      <SecWrapper
        showPriceHike={!!priceHikeInfo}
        hasPartPayment={partPaymentStatus?.is_active_part_set}
      >
        {blockPosition === 1 && <TriangleUp />}
        <Container hasIconicSection={hasIconicSection}>
          <H2>{headerText}</H2>
          {hasIconicSection && <SubHeading>{subHeading}</SubHeading>}
          <CardsContainer>
            {staticData.map((card) => (
              <Card card={card} key={card.name} />
            ))}
          </CardsContainer>
          {!partPaymentStatus?.is_active_part_set && (
            <>
              <UNDivider />
              <Pricing>
                <PrciningVal>
                  <PricingTitle>
                    {`${extraBlockInfo?.goalName} ${
                      hasIconicSection ? '- LITE ' : ''
                    }subscription starts `}
                    <br /> from
                    <ExamPricing>
                      {`${goalCurrencyCode}${textHelper.commas(
                        Math.round(extraBlockInfo?.lowestPrice)
                      )}`}
                      /month
                    </ExamPricing>
                  </PricingTitle>
                </PrciningVal>
                <LinkContainer>
                  <UnderlineLink
                    color="var(--color-i-green)"
                    href="/goal/[goalSlug]/[goalUID]/subscribe"
                    as={`/goal/${goalSlug}/${goalUID}/subscribe`}
                    onClick={handleAnalytics}
                  >
                    <ViewPricing
                      color="var(--color-i-green)"
                      data-analytics={`${dataAnalytics}-viewPricing`}
                    >
                      View pricing
                    </ViewPricing>
                  </UnderlineLink>
                </LinkContainer>
              </Pricing>
            </>
          )}
          {priceHikeInfo && (
            <PriceHike
              iconURL={priceHikeInfo?.icon}
              message={priceHikeInfo?.priceHikeText}
              borderRadius={`0px 0px ${Spacings.SPACING_2B} ${Spacings.SPACING_2B}`}
            />
          )}
        </Container>
      </SecWrapper>
    </>
  );
};

LiteSection.propTypes = {
  block: PropTypes.objectOf(PropTypes.any).isRequired,
  goalUID: PropTypes.string.isRequired,
  goalSlug: PropTypes.string.isRequired,
  goalDetails: PropTypes.objectOf(PropTypes.any),
  hasIconicSection: PropTypes.bool.isRequired,
  dataAnalytics: PropTypes.string
};

LiteSection.defaultProps = {
  dataAnalytics: '',
  goalDetails: {}
};

export default LiteSection;
